import React from 'react';

export interface StationeryCapabilityPoint {
  label: string;
  detail: string;
  icon?: React.ReactNode;
}

export interface StationeryCapabilityPanel {
  eyebrow: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  points: StationeryCapabilityPoint[];
  accent?: 'blue' | 'green' | 'amber';
  rotation?: string;
  size?: 'default' | 'wide';
}

interface StationeryCapabilityBoardProps {
  panels: StationeryCapabilityPanel[];
  variant?: 'editorial' | 'trio';
  className?: string;
}

const ACCENT_CLASS: Record<NonNullable<StationeryCapabilityPanel['accent']>, string> = {
  blue: 'stationery-panel--blue',
  green: 'stationery-panel--green',
  amber: 'stationery-panel--amber',
};

const StationeryCapabilityBoard: React.FC<StationeryCapabilityBoardProps> = ({
  panels,
  variant = 'editorial',
  className = '',
}) => {
  return (
    <div className={`stationery-board stationery-board--${variant} ${className}`.trim()}>
      {panels.map((panel) => {
        const accentClass = ACCENT_CLASS[panel.accent || 'blue'];
        const sizeClass = panel.size === 'wide' ? 'stationery-panel--wide' : '';
        return (
          <article
            key={panel.title}
            className={`stationery-panel ${accentClass} ${sizeClass}`.trim()}
            style={panel.rotation ? ({ '--panel-rotation': panel.rotation } as React.CSSProperties) : undefined}
          >
            <div className="stationery-panel__pin" aria-hidden="true" />
            <div className="stationery-panel__tape" aria-hidden="true" />
            <div className="stationery-panel__doodle" aria-hidden="true" />
            <div className="stationery-panel__top">
              <div className="stationery-panel__badge">{panel.icon}</div>
              <div>
                <p className="stationery-panel__eyebrow">{panel.eyebrow}</p>
                <h3 className="stationery-panel__title">{panel.title}</h3>
              </div>
            </div>
            <p className="stationery-panel__desc">{panel.description}</p>
            <ul className="stationery-panel__list">
              {panel.points.map((point) => (
                <li key={point.label} className="stationery-panel__item">
                  <span className="stationery-panel__item-icon" aria-hidden="true">
                    {point.icon || panel.icon}
                  </span>
                  <div>
                    <p className="stationery-panel__item-label">{point.label}</p>
                    <p className="stationery-panel__item-detail">{point.detail}</p>
                  </div>
                </li>
              ))}
            </ul>
          </article>
        );
      })}
    </div>
  );
};

export default StationeryCapabilityBoard;
